import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { useRef } from "react";
import {
  ShieldCheck,
  MapPin,
  Clock,
  HeartHandshake,
  GraduationCap,
  Sparkles,
  ArrowRight,
} from "lucide-react";
import fachada from "@/assets/espaco/why-crp.webp";
import equipe from "@/assets/espaco-novo/foto-07.webp";
import sala1 from "@/assets/espaco-novo/foto-08.webp";
import clinica1 from "@/assets/espaco-novo/foto-09.webp";
import clinica2 from "@/assets/espaco-novo/foto-10.webp";
import espera1 from "@/assets/espaco-novo/foto-11.webp";

const diferenciais = [
  {
    icon: ShieldCheck,
    eyebrow: "01 — Ética",
    title: "Registro no CRP/SP",
    text: "Todas as profissionais atuam regularmente registradas no Conselho Regional de Psicologia de São Paulo, sob responsabilidade técnica.",
    image: fachada,
    accent: "from-primary/70 via-primary/40 to-transparent",
  },
  {
    icon: GraduationCap,
    eyebrow: "02 — Formação",
    title: "Equipe especializada",
    text: "Psicólogas com formação em Psicoterapia Breve Focal, TCC, Neuropsicologia, Avaliação Psicológica e atendimento por faixa etária.",
    image: equipe,
    accent: "from-accent/70 via-primary/40 to-transparent",
  },
  {
    icon: HeartHandshake,
    eyebrow: "03 — Cuidado",
    title: "Acolhimento humanizado",
    text: "Escuta sem julgamento, ritmo respeitado e plano terapêutico individualizado — não trabalhamos com fórmulas prontas.",
    image: sala1,
    accent: "from-primary/70 via-accent/50 to-transparent",
  },
  {
    icon: MapPin,
    eyebrow: "04 — Acesso",
    title: "Localização privilegiada",
    text: "Consultório no Ipiranga, São Paulo, próximo ao metrô Sacomã — fácil acesso por transporte público e estacionamento na região.",
    image: clinica1,
    accent: "from-accent/70 via-accent/40 to-transparent",
  },
  {
    icon: Clock,
    eyebrow: "05 — Flexibilidade",
    title: "Horários amplos",
    text: "Atendemos de segunda a sexta das 8h às 21h e aos sábados das 8h às 16h — incluindo teleatendimento por videochamada.",
    image: clinica2,
    accent: "from-primary/60 via-accent/40 to-transparent",
  },
  {
    icon: Sparkles,
    eyebrow: "06 — Confiança",
    title: "Sigilo absoluto",
    text: "Tudo o que é tratado em sessão é protegido pelo Código de Ética Profissional do Psicólogo. Sua história nunca sai do nosso consultório.",
    image: espera1,
    accent: "from-accent/70 via-primary/40 to-transparent",
  },
];

const WhyChooseUs = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  const n = diferenciais.length;
  const maxTranslate = -((n - 1) / n) * 100;
  const rawX = useTransform(scrollYProgress, [0, 1], [`0%`, `${maxTranslate}%`]);
  const x = useSpring(rawX, { stiffness: 120, damping: 24, mass: 0.4 });
  const progressWidth = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <section
      ref={containerRef}
      aria-labelledby="por-que-equilybra"
      className="relative bg-background"
      style={{ height: `${n * 100}vh` }}
    >
      <div className="sticky top-0 h-screen w-full overflow-hidden">
        {/* Header overlay */}
        <div className="pointer-events-none absolute top-0 left-0 right-0 z-20 pt-6 md:pt-8 px-4 md:px-8">
          <div className="max-w-7xl mx-auto flex items-end justify-between gap-4">
            <div>
              <p className="text-white/80 font-semibold uppercase tracking-[0.25em] text-[10px] md:text-xs mb-1 drop-shadow">
                Diferenciais
              </p>
              <h2
                id="por-que-equilybra"
                className="font-heading text-2xl md:text-4xl font-bold text-white drop-shadow-lg"
              >
                Por que escolher a Equilybra?
              </h2>
            </div>
            <div className="hidden md:flex items-center gap-2 text-white/80 text-[10px] uppercase tracking-[0.25em] font-semibold drop-shadow">
              Role para explorar <ArrowRight className="w-3.5 h-3.5" />
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="pointer-events-none absolute bottom-6 left-0 right-0 z-20 px-4 md:px-8">
          <div className="max-w-7xl mx-auto flex items-center gap-4">
            <span className="text-white/80 text-xs font-semibold tabular-nums drop-shadow">
              01
            </span>
            <div className="flex-1 h-[2px] bg-white/25 rounded-full overflow-hidden backdrop-blur">
              <motion.div
                style={{ width: progressWidth }}
                className="h-full bg-gradient-to-r from-primary to-accent rounded-full"
              />
            </div>
            <span className="text-white/80 text-xs font-semibold tabular-nums drop-shadow">
              0{n}
            </span>
          </div>
        </div>

        {/* Horizontal track */}
        <motion.div
          style={{ x, width: `${n * 100}%` }}
          className="flex h-full will-change-transform"
        >
          {diferenciais.map((item, i) => (
            <CardSlide
              key={item.title}
              item={item}
              Icon={item.icon}
              index={i}
              total={n}
              scrollYProgress={scrollYProgress}
            />
          ))}
        </motion.div>
      </div>
    </section>
  );
};

interface CardSlideProps {
  item: (typeof diferenciais)[number];
  Icon: typeof ShieldCheck;
  index: number;
  total: number;
  scrollYProgress: ReturnType<typeof useScroll>["scrollYProgress"];
}

const CardSlide = ({ item, Icon, index, total, scrollYProgress }: CardSlideProps) => {
  const segStart = index / total;
  const segEnd = (index + 1) / total;
  const mid = (segStart + segEnd) / 2;

  const imageScale = useTransform(
    scrollYProgress,
    [segStart - 0.1, mid, segEnd + 0.1],
    [1.15, 1, 1.15]
  );
  const contentY = useTransform(scrollYProgress, [segStart, mid, segEnd], [80, 0, -80]);
  const contentOpacity = useTransform(
    scrollYProgress,
    [segStart - 0.05, mid, segEnd + 0.05],
    [0.2, 1, 0.2]
  );

  return (
    <div
      style={{ width: `${100 / total}%` }}
      className="relative h-full shrink-0 overflow-hidden"
    >
      <motion.div style={{ scale: imageScale }} className="absolute inset-0">
        <img
          src={item.image}
          alt={item.title}
          className="w-full h-full object-cover"
          loading={index === 0 ? "eager" : "lazy"}
        />
      </motion.div>

      <div className="absolute inset-0 bg-gradient-to-t from-[hsl(210,29%,8%)]/95 via-[hsl(210,29%,12%)]/55 to-[hsl(210,29%,15%)]/20" />
      <div
        className={`absolute inset-0 bg-gradient-to-tr ${item.accent} opacity-60 mix-blend-soft-light`}
      />

      <div className="pointer-events-none absolute -bottom-6 md:-bottom-10 -right-2 md:right-6 font-heading text-[26vh] md:text-[38vh] leading-none font-black text-white/10 select-none">
        0{index + 1}
      </div>

      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 h-full flex items-center"
      >
        <div className="max-w-7xl mx-auto w-full px-6 md:px-12 text-white">
          <div className="max-w-2xl">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl backdrop-blur-xl bg-white/15 border border-white/25 flex items-center justify-center shadow-lg">
                <Icon className="w-7 h-7 md:w-8 md:h-8 text-white" />
              </div>
              <span className="text-[10px] md:text-xs font-semibold uppercase tracking-[0.3em] text-white/80">
                {item.eyebrow}
              </span>
            </div>

            <h3 className="font-heading text-5xl md:text-7xl lg:text-8xl font-bold leading-[0.95] tracking-tight mb-6 drop-shadow-2xl">
              {item.title}
            </h3>

            <div className="w-20 h-[3px] bg-gradient-to-r from-white to-white/0 mb-6 rounded-full" />

            <p className="text-lg md:text-xl text-white/90 leading-relaxed font-body max-w-xl">
              {item.text}
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default WhyChooseUs;
